import React, { ReactNode, useState, useEffect } from "react";

interface MobileFrameProps {
  children: ReactNode;
  width?: number;
  className?: string;
  glowColor?: string;
  borderColor?: string;
  notchStyle?: "pill" | "dynamic" | "none";
  breakpoint?: number;
}

const MobileFrame: React.FC<MobileFrameProps> = ({
  children,
  width = 400,
  className = "",
  glowColor,
  borderColor,
  breakpoint = 768,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [viewportWidth, setViewportWidth] =
    useState<number | null>(null);

  useEffect(() => {
    const updateViewport = () => {
      setViewportWidth(window.innerWidth);
    };

    updateViewport();

    window.addEventListener(
      "resize",
      updateViewport
    );

    return () => {
      window.removeEventListener(
        "resize",
        updateViewport
      );
    };
  }, []);

  /*
   * Mobile / Tablet / Narrow Browser
   *
   * Use the actual browser viewport.
   *
   * 100dvh is important because mobile browser
   * address bars can dynamically appear/disappear.
   */
  const shouldRenderFullBleed =
    viewportWidth !== null &&
    viewportWidth < breakpoint;

  if (shouldRenderFullBleed) {
    return (
      <div
        className="w-full overflow-hidden bg-white"
        style={{
          width: "100%",
          height: "100dvh",
          minHeight: "100dvh",
          maxHeight: "100dvh",

          paddingTop:
            "env(safe-area-inset-top)",

          paddingBottom:
            "env(safe-area-inset-bottom)",

          paddingLeft:
            "env(safe-area-inset-left)",

          paddingRight:
            "env(safe-area-inset-right)",

          boxSizing: "border-box",
        }}
      >
        <div
          className="w-full h-full overflow-auto"
          style={{
            overscrollBehavior: "none",
            WebkitOverflowScrolling: "touch",
          }}
          onScroll={(e) => {
            setIsScrolled(
              e.currentTarget.scrollTop > 5
            );
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  /*
   * Prevent rendering with unknown viewport dimensions.
   */
  if (viewportWidth === null) {
    return null;
  }

  const actualGlowColor =
    glowColor || "#a5b4fc";

  const actualBorderColor =
    borderColor || "#e5e7eb";

  const hexToRgba = (
    hex: string,
    alpha: number
  ) => {
    const r = parseInt(
      hex.slice(1, 3),
      16
    );

    const g = parseInt(
      hex.slice(3, 5),
      16
    );

    const b = parseInt(
      hex.slice(5, 7),
      16
    );

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleScroll = (
    e: React.UIEvent<HTMLDivElement>
  ) => {
    setIsScrolled(
      e.currentTarget.scrollTop > 5
    );
  };

  /*
   * Desktop / large tablet.
   *
   * Keep some horizontal breathing room so the
   * frame can never overflow the browser.
   */
  const effectiveWidth = Math.min(
    width,
    viewportWidth - 32
  );

  return (
    <div
      className="flex items-center justify-center w-full overflow-hidden bg-gray-50 p-4"
      style={{
        height: "100dvh",
        minHeight: "100dvh",
        maxHeight: "100dvh",
        boxSizing: "border-box",
      }}
    >
      <div
        id="mobile-frame"
        className={`relative overflow-hidden rounded-[2.5rem] ${className}`}
        style={{
          width: "100%",
          maxWidth: `${effectiveWidth}px`,
          minWidth: "300px",

          height: "min(92dvh, 760px)",

          backgroundColor: "#ffffff",

          boxShadow: `
            0 20px 50px rgba(0,0,0,0.08),
            0 0 30px ${hexToRgba(
              actualGlowColor,
              0.08
            )}
          `,

          border: `1px solid ${actualBorderColor}`,
        }}
      >
        <div
          className="relative w-full h-full overflow-auto"
          onScroll={handleScroll}
          style={{
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Top fade */}
          <div
            className={`absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none ${
              isScrolled
                ? "opacity-60"
                : "opacity-0"
            }`}
          />

          {children}
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none" />

        {/* Home indicator */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-28 h-1 bg-gray-300 rounded-full opacity-70" />
      </div>
    </div>
  );
};

export default MobileFrame;